/**
 * Permissões e resolução do perfil de acesso.
 *
 * Mora no domínio porque tem dois consumidores que **não podem discordar**: o painel, que decide o
 * que exibir e habilitar, e a API, que decide o que autorizar. A guarda no painel é conveniência de
 * navegação; a decisão que vale é a do servidor. Se as duas lerem a mesma matriz, uma tela habilitada
 * indevidamente ainda é recusada no servidor.
 *
 * Ver decisões D4 (autorização híbrida) e D5 (perfil de acesso distinto do cargo de exibição).
 */

import type { PerfilAcesso } from './documento.ts';
import type { Trilha } from './tipos.ts';

// ---------------------------------------------------------------------------
// Ações
// ---------------------------------------------------------------------------

/**
 * Ações sujeitas a autorização.
 *
 * Granularidade escolhida para separar o que o negócio realmente diferencia: propostas próprias de
 * propostas de terceiros, e rascunho de documento já publicado — porque editar algo que já está nas
 * mãos de um cliente é um ato diferente de editar um rascunho.
 */
export type Acao =
  | 'ver_proprias'
  | 'ver_todas'
  | 'criar'
  | 'publicar'
  | 'editar_rascunho_proprio'
  | 'editar_publicada_propria'
  | 'editar_de_outro'
  | 'invalidar_cache'
  | 'gerenciar_usuarios'
  | 'ver_auditoria'
  | 'usar_trilha_ps';

export const ACOES: readonly Acao[] = [
  'ver_proprias',
  'ver_todas',
  'criar',
  'publicar',
  'editar_rascunho_proprio',
  'editar_publicada_propria',
  'editar_de_outro',
  'invalidar_cache',
  'gerenciar_usuarios',
  'ver_auditoria',
  'usar_trilha_ps',
];

// ---------------------------------------------------------------------------
// Matriz
// ---------------------------------------------------------------------------

/**
 * Matriz de permissões, confirmada com o usuário.
 *
 * Dado versionado, não condicionais espalhadas pelo código. Acrescentar uma ação obriga a decidir
 * explicitamente para cada papel, porque o tipo exige a chave.
 *
 * Notas de leitura:
 *
 * - `invalidar_cache` e `gerenciar_usuarios` são exclusivas de `admin`.
 * - `diretor` audita e edita publicada de qualquer pessoa, mas não administra usuários.
 * - `vendedor` cria, publica, lê as dos outros e **edita as próprias mesmo depois de publicadas**.
 *   Restringir isso esvaziaria o requisito 6, que é justamente poder corrigir proposta já enviada
 *   sem gerar link novo — e quem precisa corrigir é o vendedor.
 * - `vendedor` absorveu o antigo `inside_sales`, que era um perfil separado com menos permissão.
 *   O usuário confirmou que as duas funções são a mesma na eMaster.
 * - `leitor` só lê.
 */
export const MATRIZ_PERMISSOES: Readonly<Record<PerfilAcesso, Readonly<Record<Acao, boolean>>>> = {
  admin: {
    ver_proprias: true,
    ver_todas: true,
    criar: true,
    publicar: true,
    editar_rascunho_proprio: true,
    editar_publicada_propria: true,
    editar_de_outro: true,
    invalidar_cache: true,
    gerenciar_usuarios: true,
    ver_auditoria: true,
    usar_trilha_ps: true,
  },
  diretor: {
    ver_proprias: true,
    ver_todas: true,
    criar: true,
    publicar: true,
    editar_rascunho_proprio: true,
    editar_publicada_propria: true,
    editar_de_outro: true,
    invalidar_cache: false,
    gerenciar_usuarios: false,
    ver_auditoria: true,
    usar_trilha_ps: true,
  },
  vendedor: {
    ver_proprias: true,
    // Vendedor vê **apenas as próprias** propostas, no painel e no dashboard. Decisão do usuário em
    // 14/08/2026: cada vendedor acompanha a própria carteira; a visão consolidada é de admin e
    // diretor. Isso também evita que o dashboard exponha o volume de negociação de um colega.
    ver_todas: false,
    criar: true,
    publicar: true,
    editar_rascunho_proprio: true,
    editar_publicada_propria: true,
    editar_de_outro: false,
    invalidar_cache: false,
    gerenciar_usuarios: false,
    ver_auditoria: false,
    usar_trilha_ps: false,
  },
  leitor: {
    ver_proprias: true,
    ver_todas: true,
    criar: false,
    publicar: false,
    editar_rascunho_proprio: false,
    editar_publicada_propria: false,
    editar_de_outro: false,
    invalidar_cache: false,
    gerenciar_usuarios: false,
    ver_auditoria: false,
    usar_trilha_ps: false,
  },
};

/**
 * Decide se um perfil pode executar uma ação.
 *
 * `perfil` aceita `null` para representar usuário autenticado **sem papel atribuído** — situação real
 * no pool, onde um usuário está confirmado e sem `custom:role`. Nesse caso nada é permitido.
 */
export function pode(perfil: PerfilAcesso | null, acao: Acao): boolean {
  if (perfil === null) return false;
  return MATRIZ_PERMISSOES[perfil][acao];
}

export interface ContextoProposta {
  /** `sub` do responsável pela proposta. */
  readonly responsavelSub: string;
  readonly publicada: boolean;
}

/**
 * Decide se um perfil pode editar uma proposta específica.
 *
 * Concentra aqui a combinação de dono e situação, que é onde erros de autorização costumam nascer
 * quando a regra fica espalhada por telas e handlers.
 */
export function podeEditarProposta(
  perfil: PerfilAcesso | null,
  usuarioSub: string,
  proposta: ContextoProposta,
): boolean {
  if (perfil === null) return false;

  const ehDono = proposta.responsavelSub === usuarioSub;

  if (!ehDono) return pode(perfil, 'editar_de_outro');
  return proposta.publicada
    ? pode(perfil, 'editar_publicada_propria')
    : pode(perfil, 'editar_rascunho_proprio');
}

/** Decide se um perfil pode ver uma proposta específica. */
export function podeVerProposta(
  perfil: PerfilAcesso | null,
  usuarioSub: string,
  proposta: ContextoProposta,
): boolean {
  if (perfil === null) return false;
  if (proposta.responsavelSub === usuarioSub) return pode(perfil, 'ver_proprias');
  return pode(perfil, 'ver_todas');
}

// ---------------------------------------------------------------------------
// Tradução dos valores legados
// ---------------------------------------------------------------------------

/**
 * Traduz os valores de `custom:role` em uso no pool para o enum.
 *
 * Valores reais observados nos 11 usuários: `Admin`, `Manager`, `Inside Sales` e ausente. A
 * comparação é tolerante a caixa e a espaço excedente, porque o campo é texto livre e foi preenchido
 * à mão no console.
 *
 * `Inside Sales` traduz para `vendedor`: são a mesma função na eMaster, e o perfil `inside_sales`
 * deixou de existir. Os 6 usuários habilitados com esse valor legado passam a ser vendedores, o que
 * lhes dá leitura das propostas dos colegas e edição das próprias já publicadas.
 *
 * Valor desconhecido devolve `null`, que significa **ausência de permissão**. Nunca um perfil padrão:
 * um erro de digitação no console não pode virar acesso concedido.
 */
export function traduzirPapelLegado(bruto: string | null | undefined): PerfilAcesso | null {
  if (bruto === null || bruto === undefined) return null;

  const chave = bruto.trim().toLowerCase().replace(/\s+/g, ' ');

  switch (chave) {
    case 'admin':
      return 'admin';
    case 'manager':
      return 'diretor';
    case 'inside sales':
    case 'inside_sales':
    case 'vendedor':
    case 'sales':
      return 'vendedor';
    case 'leitor':
    case 'reader':
      return 'leitor';
    default:
      return null;
  }
}

/** Nome do grupo do Cognito correspondente ao perfil. Fonte de verdade da autorização (D4). */
export function grupoDoPerfil(perfil: PerfilAcesso): string {
  return perfil;
}

/**
 * Valor a gravar em `custom:role`, preservando o formato que o legado espera.
 *
 * `vendedor` grava **`Inside Sales`**, e não `Vendedor`, deliberadamente: o painel legado compartilha
 * este User Pool e reconhece o valor `Inside Sales`. Gravar um valor novo faria o legado deixar de
 * identificar o papel do usuário, quebrando quem ainda estiver usando a versão antiga durante a
 * transição. O enum interno é `vendedor`; a representação externa continua sendo a legada até o
 * desligamento do legado.
 */
export function papelLegadoDoPerfil(perfil: PerfilAcesso): string {
  const mapa: Readonly<Record<PerfilAcesso, string>> = {
    admin: 'Admin',
    diretor: 'Manager',
    vendedor: 'Inside Sales',
    leitor: 'Leitor',
  };
  return mapa[perfil];
}

// ---------------------------------------------------------------------------
// Resolução do perfil a partir do token
// ---------------------------------------------------------------------------

export interface PerfilResolvido {
  readonly perfil: PerfilAcesso | null;
  /** De onde veio a decisão, para diagnóstico. */
  readonly origem: 'grupo' | 'custom_role' | 'nenhuma';
  /**
   * `true` quando grupo e `custom:role` apontam para perfis diferentes.
   * Serve de detector de dessincronização durante a migração.
   */
  readonly divergente: boolean;
}

/**
 * Resolve o perfil de acesso a partir dos claims do token.
 *
 * Precedência: **grupo do Cognito ganha**. O grupo não é gravável pelo próprio usuário e seu claim
 * chega no `accessToken`, que é o token validado pelo autorizador da API. O `custom:role` é aceito
 * apenas como retaguarda durante a migração, porque hoje nenhum grupo existe no pool — e é
 * auto-editável pelo usuário (risco R2), portanto não serve como fonte de verdade a longo prazo.
 *
 * @param grupos Claim `cognito:groups` do `accessToken`.
 * @param customRole Claim `custom:role` do `idToken`.
 */
export function resolverPerfil(
  grupos: readonly string[] | null | undefined,
  customRole: string | null | undefined,
): PerfilResolvido {
  const doGrupo = perfilDeGrupos(grupos);
  const doAtributo = traduzirPapelLegado(customRole);

  const divergente = doGrupo !== null && doAtributo !== null && doGrupo !== doAtributo;

  if (doGrupo !== null) return { perfil: doGrupo, origem: 'grupo', divergente };
  if (doAtributo !== null) return { perfil: doAtributo, origem: 'custom_role', divergente };
  return { perfil: null, origem: 'nenhuma', divergente: false };
}

/**
 * Escolhe o perfil de maior privilégio entre os grupos do usuário.
 *
 * Um usuário pode pertencer a vários grupos. Sem uma ordem explícita, a resolução dependeria da
 * ordem de retorno da API, que não é garantida.
 *
 * `inside_sales` é aceito como **alias** de `vendedor` para o caso de alguém ter criado o grupo antes
 * desta consolidação. Não é um perfil, é tolerância de entrada.
 */
function perfilDeGrupos(grupos: readonly string[] | null | undefined): PerfilAcesso | null {
  if (grupos === null || grupos === undefined || grupos.length === 0) return null;

  const normalizados = new Set(grupos.map((g) => g.trim().toLowerCase()));

  const precedencia: readonly { grupo: string; perfil: PerfilAcesso }[] = [
    { grupo: 'admin', perfil: 'admin' },
    { grupo: 'diretor', perfil: 'diretor' },
    { grupo: 'vendedor', perfil: 'vendedor' },
    { grupo: 'inside_sales', perfil: 'vendedor' },
    { grupo: 'leitor', perfil: 'leitor' },
  ];

  return precedencia.find((p) => normalizados.has(p.grupo))?.perfil ?? null;
}

// ---------------------------------------------------------------------------
// Trilha comercial
// ---------------------------------------------------------------------------

/**
 * Grupo do Cognito que define a trilha de setor público.
 *
 * Existe porque a trilha não é questão de hierarquia, e sim de atribuição comercial: quem tem
 * carteira de setor público trabalha em PS, e quem não tem trabalha em CS.
 */
export const GRUPO_SETOR_PUBLICO = 'setor_publico';

/** Grupo que concede a trilha comercial. Quem não tem grupo algum atua em CS, por padrão. */
export const GRUPO_COMERCIAL = 'comercial';

/**
 * Trilhas que a pessoa pode usar.
 *
 * Três configurações possíveis, decididas pelos grupos do Cognito:
 *
 * | Grupos                          | Trilhas      |
 * | ------------------------------- | ------------ |
 * | nenhum, ou só `comercial`       | `['CS']`     |
 * | só `setor_publico`              | `['PS']`     |
 * | `setor_publico` e `comercial`    | `['CS','PS']`|
 *
 * O padrão é CS: quem entra na empresa sem atribuição de setor público vende comercial. Isso
 * mantém o comportamento seguro por omissão — ninguém ganha a trilha PS por acidente, porque a
 * margem e a moeda dela são diferentes.
 */
export function trilhasDaPessoa(grupos: readonly string[] | null | undefined): readonly Trilha[] {
  const normalizados = (grupos ?? []).map((g) => g.trim().toLowerCase());
  const temPs = normalizados.includes(GRUPO_SETOR_PUBLICO);
  const temCs = normalizados.includes(GRUPO_COMERCIAL);

  if (temPs && temCs) return ['CS', 'PS'];
  if (temPs) return ['PS'];
  return ['CS'];
}

/** Decide se o solicitante pode emitir proposta na trilha informada. */
export function podeUsarTrilha(
  grupos: readonly string[] | null | undefined,
  trilha: Trilha,
): boolean {
  return trilhasDaPessoa(grupos).includes(trilha);
}

/** Compatibilidade: responde se a pessoa atua em PS. */
export function podeUsarTrilhaPs(
  perfil: PerfilAcesso | null,
  grupos: readonly string[] | null | undefined,
): boolean {
  if (perfil === null) return false;
  return trilhasDaPessoa(grupos).includes('PS');
}
